import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./LoginPage.css"; // Reuse existing styles

export default function CompleteProfilePage({ user, setHasProfile }) {
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [name, setName] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [postcode, setPostcode] = useState("");
  const [suburb, setSuburb] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [suburbLoading, setSuburbLoading] = useState(false); // 🆕 loading state
  const [error, setError] = useState("");

  const isFormComplete = username.trim() && name.trim() && postcode.trim() && suburb.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormComplete) {
      setError("Please fill out all fields.");
      return;
    }

    try {
      const res = await fetch("/api/profile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ username, name, suburb, postcode })
      });

      if (res.ok) {
        setHasProfile(true);
        navigate("/profile", { replace: true, state: { firstLogin: true } });
      } else {
        const data = await res.json();
        setError(data.error || "Failed to save profile.");
      }
    } catch (err) {
      console.error("❌ Profile save error:", err);
      setError("Something went wrong. Please try again.");
    }
  };

  useEffect(() => {
    if (searchInput.length >= 3) {
      setSuburbLoading(true);
      fetch(
        `https://secure.geonames.org/postalCodeSearchJSON?placename=${encodeURIComponent(
          searchInput
        )}&country=AU&maxRows=5&username=kungphukenobi`
      )
        .then((res) => res.json())
        .then((data) => {
          if (data?.postalCodes) {
            setSuggestions(data.postalCodes);
          }
        })
        .catch((err) => {
          console.error("❌ Geonames lookup error:", err);
        })
        .finally(() => {
          setSuburbLoading(false);
        });
    } else {
      setSuggestions([]);
      setSuburbLoading(false);
    }
  }, [searchInput]);

  const handleSuggestionClick = (suggestion) => {
    setSuburb(suggestion.placeName);
    setPostcode(suggestion.postalCode);
    setSearchInput(`${suggestion.placeName} (${suggestion.postalCode})`);
    setSuggestions([]);
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <img
          src="/campwithfriends-logo.png"
          alt="Camp With Friends Logo"
          style={{ width: "200px", marginBottom: "20px" }}
        />

        <h2 style={{ marginBottom: "10px" }}>Welcome {user?.email || ""}!</h2>
        <p style={{ marginBottom: "20px", fontSize: "16px" }}>
          Let's get a couple of details before getting you in to set up your crew, rides and gear!
        </p>

        <form className="login-form" onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <input
            type="text"
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          {/* 🆕 Suburb/Postcode Autocomplete Input */}
          <input
            type="text"
            placeholder="Suburb or Postcode"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            autoComplete="off"
          />

          {/* 🆕 Spinner while loading */}
          {suburbLoading && (
            <div
              style={{
                marginTop: "8px",
                marginBottom: "8px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  border: "4px solid #f3f3f3",
                  borderTop: "4px solid #2c7373",
                  borderRadius: "50%",
                  width: "24px",
                  height: "24px",
                  animation: "spin 1s linear infinite"
                }}
              ></div>
            </div>
          )}

          {suggestions.length > 0 && (
            <div style={{
              backgroundColor: "#fff",
              color: "#000",
              borderRadius: "6px",
              marginTop: "4px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              maxHeight: "200px",
              overflowY: "auto",
              fontSize: "14px"
            }}>
              {suggestions.map((sugg, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: "10px",
                    borderBottom: "1px solid #eee",
                    cursor: "pointer"
                  }}
                  onClick={() => handleSuggestionClick(sugg)}
                >
                  {sugg.placeName} ({sugg.postalCode})
                </div>
              ))}
            </div>
          )}

          <button
            type="submit"
            disabled={!isFormComplete}
            className={!isFormComplete ? "disabled-button" : ""}
          >
            Save and Continue
          </button>

          {error && <div className="error-message">{error}</div>}
        </form>

        {/* 🆕 Add the spinning animation */}
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    </div>
  );
}
